import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { ErrorOutline, Refresh, Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ErrorHandler from '../../Services/ErrorHandler/ErrorHandler';

/**
 * Error Boundary 컴포넌트
 * React 컴포넌트 트리에서 발생한 JavaScript 에러를 캐치
 * 
 * 주의: Error Boundary는 다음을 잡지 못함
 * - 이벤트 핸들러 에러
 * - 비동기 코드 에러 (setTimeout, Promise 등)
 * - 서버 사이드 렌더링 에러
 * - 자기 자신에서 발생한 에러
 */
class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // 다음 렌더에서 fallback UI가 보이도록 state를 업데이트합니다.
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // 에러 리포팅 서비스에 에러를 기록할 수 있습니다.
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // 개발자에게 에러 메일 전송
    const errorDetails = {
      type: 'RENDER_ERROR',
      statusCode: 0,
      message: error.message || '컴포넌트 렌더링 오류',
      originalMessage: error.message || 'Unknown Error',
      stack: error.stack || 'N/A',
      componentStack: errorInfo?.componentStack || 'N/A',
    };

    // 비동기로 처리하여 UI 블로킹 방지
    setTimeout(async () => {
      try {
        // await ErrorHandler.sendErrorEmailToDeveloper(errorDetails, {
        //   url: window.location.href,
        //   method: 'render',
        // });
      } catch (emailError) {
        console.error('에러 메일 전송 실패:', emailError);
      }
    }, 0);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // 커스텀 fallback UI를 렌더링할 수 있습니다.
      return (
        <ErrorFallbackUI
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          fallback={this.props.fallback}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * 에러 발생 시 표시할 Fallback UI (Router 내부에서 사용하므로 useNavigate 사용 가능)
 */
function ErrorFallbackUI({ error, errorInfo, onReset, fallback }) {
  const navigate = useNavigate();

  // 커스텀 fallback이 있으면 그것을 사용
  if (fallback) {
    return typeof fallback === 'function' ? fallback(error, errorInfo, onReset) : fallback;
  }

  const handleGoHome = () => {
    onReset();
    navigate('/Home');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 4,
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        <ErrorOutline sx={{ fontSize: 64, color: '#d32f2f', mb: 2 }} />
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          오류가 발생했습니다
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          애플리케이션에서 예기치 않은 오류가 발생했습니다.
          <br />
          페이지를 새로고침하거나 홈으로 이동해주세요.
        </Typography>

        {process.env.NODE_ENV === 'development' && error && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: '#fafafa',
              borderRadius: 1,
              textAlign: 'left',
              mb: 3,
            }}
          >
            <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
              {error.toString()}
              {errorInfo?.componentStack && `\n\n${errorInfo.componentStack}`}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={onReset}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            다시 시도
          </Button>
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={handleGoHome}
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            홈으로 이동
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

/**
 * Error Boundary HOC (함수형 컴포넌트를 위한 래퍼)
 */
function ErrorBoundary({ children, fallback }) {
  return <ErrorBoundaryClass fallback={fallback}>{children}</ErrorBoundaryClass>;
}

export default ErrorBoundary;

